import { Component, Inject, OnInit, Renderer2, HostListener, DOCUMENT } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { cultureLanguageIds, languages } from 'src/app/types/language.type';
import { LanguageService } from 'src/app/services/language/language.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CountryService } from 'src/app/services/country/country.service';
import { AuthService } from '@auth/auth.service';
import { WINDOW_OBJECT } from 'src/app/services/window/window.service';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { take, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { BaseComponent } from '../base/component/base-component';
import { LanguageId } from 'src/app/types/language.type';
import { combineLatest } from 'rxjs';
import { LocalStorageService } from 'src/app/services/storage/storage.service';
// import { PatientService } from 'src/app/services/patient/patient.service';
// import { take } from 'rxjs/operators';
import { AlertService } from 'src/app/modules/alert/components/services/alert.service';
import { FeatureMetadataService } from 'src/app/services/feature-metadata/feature-metadata.service';

@Component({
  selector: 'mpp-root',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  standalone: false,
})
export class MainComponent extends BaseComponent implements OnInit {
  layout = 'default';
  private oneTrustCookieBannerIsLoaded = false;
  public showOneTrustFooter: boolean;
  public defaultLanguageSelect: LanguageId;
  public chatbotButtonVisible: boolean = false;
  public chatbotLoaded: boolean = false;
  public chatbotVisible: boolean = false;
  public chatbotTextLoaded: boolean = false;
  public chatbotTextVisible: boolean = false;
  public chatbotWelcomeBack: boolean = false;
  public chatbotUrls: any = undefined;
  private notAppended: boolean = true;
  private preloadedUserData: { name: string; email: string; patientId?: string } | null = null;
  private dawnScriptLoaded: boolean = false;
  public showDawnButtonAnimation: boolean = false;

  constructor(
    public translate: TranslateService,
    private languageService: LanguageService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private countryService: CountryService,
    private authService: AuthService,
    private analyticsService: AnalyticsService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(WINDOW_OBJECT) public window: Window,
    private renderer: Renderer2,
    public localStorageService: LocalStorageService,
    // private patientService: PatientService,
    private alertService: AlertService,
    private featureMetadataService: FeatureMetadataService,
  ) {
    super();
    translate.addLangs([...cultureLanguageIds]);
  }

  async ngOnInit(): Promise<void> {
    this.initializeChatbotListener();

    // Show blue footer for AMR, hide it for EU
    if (!environment.isEU) {
      this.showOneTrustFooter = true;
      this.defaultLanguageSelect = 'EnglishUs';
    } else {
      this.showOneTrustFooter = false;
      this.defaultLanguageSelect = 'EnglishUk';
    }
    this.languageService.checkAndSetLanguageId(this.defaultLanguageSelect);

    this.countryService
      .loadCountries()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data) => {
        this.countryService.checkAndSetCountryId();
      });

    this.languageService.selectedLanguageId$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((language) => {
        this.translate.use(languages[language]);
        this.setOneTrustLanguage();
      });

    this.authService.authenticatedUser$.pipe(takeUntil(this.unsubscribe$)).subscribe((user) => {
      if (user) {
        this.analyticsService.checkAndSetAnalytics();
        this.languageService.checkAndSetCountryAvailableLanguage(
          this.countryService.getSelectedCountry(),
        );
        // Pre-fetch feature metadata for US users to warm Apollo cache
        // so the support page HIPAA FAQ check resolves instantly
        if (this.countryService.getSelectedCountryId() === 'US') {
          this.featureMetadataService.getFeatureMetadata().pipe(take(1)).subscribe();
        }
      }
    });

    this.countryService.selectedCountryId$.pipe(takeUntil(this.unsubscribe$)).subscribe((_) => {
      if (!this.countryService.getSelectedCountry()) return;

      this.appendOneTrustCookieBanner();

      // if AMR, run function to determine whether footer hides (non-CPRA) or shows (CPRA)
      if (!environment.isEU) {
        this.hideShowOneTrustFooterButton();
      }
    });

    this.router.events.pipe(takeUntil(this.unsubscribe$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.layout = this.activatedRoute.firstChild.firstChild.snapshot.data.layout || 'default';
      }
    });
    this.authService.getUserInfo();
    this.authService.setOnRenewedHandler();
  }

  @HostListener('document:click', ['$event'])
  public handleClick(event: Event): void {
    if (event.target instanceof HTMLAnchorElement) {
      const element = event.target as HTMLAnchorElement;
      if (element.className.includes('myair-routerlink')) {
        event.preventDefault();
        const route = element?.getAttribute('href');
        if (route) {
          this.router.navigate([`/${route}`]);
        }
      }
    }
  }

  async loadDawnAIChatbot(countryId: string): Promise<void> {
    const dawnURL = countryId === 'US' ? environment.dawnURLUS : environment.dawnURLAU;
    // console.log(`Dawn chatbot loading for country: ${countryId}, URL: ${dawnURL}`);
    if (dawnURL && !this.chatbotUrls) {
      try {
        const response = await fetch(dawnURL);
        const urls = await response.json();
        // Store the fetched URLs so we don't fetch them again
        this.chatbotUrls = urls;
      } catch (error) {
        console.error('Failed to load chatbot script URLs');
        this.alertService.info();
      }
    }
  }

  async getAutoLoginUserData(): Promise<{ name: string; email: string; patientId?: string }> {
    const currentUser = this.authService.authenticatedUser$.getValue();
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    // try {
    // Used to fetch patient_id.
    // const [patientWrapper, error] = await this.patientService.getPatient().pipe(take(1)).toPromise();

    // if (error) {
    //   console.error('GraphQL error fetching patientWrapper');
    // }

    return {
      name: currentUser.FullName || `${currentUser.FirstName} ${currentUser.LastName}`.trim(),
      email: currentUser.Email,
      // patientId: patientWrapper?.patient?.id // Optional chaining. patientId not required for Dawn
    };
    // } catch (error) {
    //   console.error('Failed to get patient data for Dawn auto-login');
    //   throw error; // Re-throw so the calling code handles the failure
    // }
  }

  initializeChatbotListener(): void {
    combineLatest([
      this.countryService.selectedCountryId$,
      this.authService.isAuthenticated$,
      this.authService.authenticatedUser$,
    ]).subscribe(async ([countryId, isAuthenticated, currentUser]) => {
      const currentlyAllowed = (countryId === 'US' || countryId === 'AU') && isAuthenticated;

      if (!this.chatbotButtonVisible && currentlyAllowed && this.notAppended) {
        this.notAppended = false;

        // Fetch the chatbot script URLs based on country
        await this.loadDawnAIChatbot(countryId);

        // If authenticated but no currentUser, fetch it now
        if (!currentUser && isAuthenticated) {
          try {
            await this.authService.getUserInfo();
            currentUser = this.authService.authenticatedUser$.getValue();
          } catch (error) {
            console.error('Failed to fetch user info for Dawn');
            // currentUser stays null, will fall through to manual login
          }
        }

        // Try auto-login if we have user data
        if (currentUser) {
          try {
            this.preloadedUserData = await this.getAutoLoginUserData();
            // console.log('Preloaded user data:', this.preloadedUserData);

            (window as any).chatbotConfig = {
              name: this.preloadedUserData.name,
              email: this.preloadedUserData.email,
              // patient_id: this.preloadedUserData.patientId
            };
            // console.log('chatbotConfig set before script load:', (window as any).chatbotConfig);
          } catch (error) {
            console.error(
              'Failed to preload user data for Dawn auto-login, loading Dawn with manual login.',
            );
          }
        } else {
          console.log('No currentUser available, loading Dawn with manual login.');
        }

        this.loadDawnScript();
      }
    });
  }

  loadDawnScript(): void {
    // Check if script already loaded
    if (this.dawnScriptLoaded) {
      // console.log('Dawn script already loaded');
      return;
    }

    const existingScript = document.querySelector(
      `script[src="${this.chatbotUrls[environment.dawnEnv]}"]`,
    );
    if (existingScript) {
      // console.log('Dawn script already in DOM');
      this.dawnScriptLoaded = true;
      return;
    }

    // console.log('Loading Dawn script...');
    const script = document.createElement('script');
    script.src = this.chatbotUrls[environment.dawnEnv];
    script.onerror = (error) => {
      console.error('Dawn script failed to load:', error);
      this.alertService.info();
    };
    script.onload = () => {
      // console.log('Dawn script loaded successfully');
      // Show Dawn text, allow Dawn button
      this.chatbotTextLoaded = true;
      this.chatbotTextVisible = true;
      this.chatbotWelcomeBack = !this.localStorageService.getItem(
        this.localStorageService.dawnWelcomeBack,
      );
      this.dawnScriptLoaded = true;
      this.chatbotButtonVisible = true;
      this.chatbotLoaded = true; // Only set to true after script actually loads
    };
    document.body.appendChild(script);

    // Prevent button animation FOUC due to Dawn's initlialization triggering Angular change detection again.
    setTimeout(() => {
      this.showDawnButtonAnimation = true;
    }, 1750);
  }

  logDawnAnalytics(): void {
    this.analyticsService.logDawnAILaunched(this.countryService.getSelectedCountryId());
  }

  // Dynamically construct and append the OneTrust cookie banner on an AMR vs EU basis
  appendOneTrustCookieBanner(): void {
    if (this.oneTrustCookieBannerIsLoaded) return;

    let oneTrustEnv: string;
    let oneTrustAutoBlockSrc: string;

    // if AMR
    if (!environment.isEU) {
      oneTrustEnv = environment.oneTrustIdAMR;
      oneTrustAutoBlockSrc = `https://cdn.cookielaw.org/consent/${environment.oneTrustIdAMR}/OtAutoBlock.js`;
      // else if EU
    } else {
      oneTrustEnv = environment.oneTrustIdEU;
      oneTrustAutoBlockSrc = `https://cdn.cookielaw.org/consent/${environment.oneTrustIdEU}/OtAutoBlock.js`;
    }

    // Define OptanonWrapper before loading scripts
    if (!environment.isEU) {
      const wrapperScript = this.renderer.createElement('script');
      this.renderer.setAttribute(wrapperScript, 'type', 'text/javascript');
      wrapperScript.innerHTML = 'function OptanonWrapper() { }';
      this.renderer.appendChild(this.document.body, wrapperScript);
    }

    // STEP 1: Load otSDKStub.js FIRST (main SDK)
    const sdkScript = this.renderer.createElement('script');
    this.renderer.setAttribute(
      sdkScript,
      'src',
      'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
    );
    this.renderer.setAttribute(sdkScript, 'type', 'text/javascript');
    this.renderer.setAttribute(sdkScript, 'charset', 'UTF-8');
    this.renderer.setAttribute(sdkScript, 'data-domain-script', oneTrustEnv);

    // STEP 2: After SDK loads, load OtAutoBlock.js
    sdkScript.onload = () => {
      const autoBlockScript = this.renderer.createElement('script');
      this.renderer.setAttribute(autoBlockScript, 'src', oneTrustAutoBlockSrc);
      this.renderer.setAttribute(autoBlockScript, 'type', 'text/javascript');

      // STEP 3: After both scripts load, set language
      autoBlockScript.onload = () => {
        // Give OneTrust a moment to fully initialize before setting language
        setTimeout(() => {
          this.setOneTrustLanguage();
        }, 100);
      };

      this.renderer.appendChild(this.document.body, autoBlockScript);
    };

    this.renderer.appendChild(this.document.body, sdkScript);
    this.oneTrustCookieBannerIsLoaded = true;
  }

  setOneTrustLanguage() {
    if (this.window['OneTrust']) {
      this.window['OneTrust'].changeLanguage(
        this.languageService.getCurrent2LettersLanguage().slice(0, 2),
      );
    }
  }

  hideShowOneTrustFooterButton() {
    // Wait for OneTrust footer to render
    this.waitForElm('#ot-sdk-btn').then((elm: HTMLElement) => {
      let that = this;
      const target = document.querySelector('#ot-sdk-btn');
      const observer = new MutationObserver(mutate);
      const config = { characterData: false, attributes: false, childList: true, subtree: false };

      // Wait for OneTrust footer's .textContent to be loaded into footer
      function mutate(mutations) {
        mutations.forEach(function (mutation) {
          // If OneTrust footer contains only whitespace string (non-CPRA), hide footer. Otherwise, show footer (CPRA)
          if (elm.textContent) {
            if (!elm.textContent.trim()) {
              that.showOneTrustFooter = false;
            } else {
              that.showOneTrustFooter = true;
            }
          }
        });
      }

      observer.observe(target, config);
    });

    // Wait for OneTrust banner to render (if OneTrust banner has not been previously dismissed)
    this.waitForElm('#onetrust-button-group').then((elm: HTMLElement) => {
      if (elm.childElementCount > 1) {
        this.showOneTrustFooter = true;
      } else {
        this.showOneTrustFooter = false;
      }
    });
  }

  // Function which waits for asynchronous OneTrust cookie banner/footer HTML to render
  waitForElm(selector) {
    return new Promise((resolve) => {
      if (document.querySelector(selector)) {
        return resolve(document.querySelector(selector));
      }

      const observer = new MutationObserver((mutations) => {
        if (document.querySelector(selector)) {
          resolve(document.querySelector(selector));
          observer.disconnect();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    });
  }
}
